import type { ReactNode } from 'react';

import { formatNumber } from '@shared/format';
import { cn } from '@shared/ui/cn';
import { StateBadge } from '@shared/ui/StateBadge';

import { roleLabel } from './copy';
import type { CreditedItemRow } from './types';

/**
 * The fragments both credited-person surfaces render the same way.
 */

/**
 * The credited person's face — their portrait where one exists, the neutral
 * gradient where it does not.
 *
 * **A gradient, never an initial.** An Arabic letter changes shape with
 * joining, so a one-letter monogram reads as a different letter than the name
 * it stands for — the same rule that governs the top bar's account face.
 *
 * **`inline-grid`, not a bare inline `<span>`.** Logical sizing does not apply
 * to a non-replaced inline element, so `inline-size` on a bare span is silently
 * discarded and the disc never appears. The subscriber pack shipped exactly
 * that defect and repaired it; transcribing the repair is the point of
 * transcribing the shape.
 *
 * **The portrait belongs here now**, and its absence was the defect: `CREDIT_PROFILE`
 * is one of the artwork subjects, the person's own form uploads against it and
 * PATCHes `portrait_url` back, and this face went on drawing the gradient over a
 * column that had just been filled. An operator who replaced the image saw the
 * control beside the form update and the head above it stay black — one record,
 * two answers on one screen. The head reads the same column the control writes,
 * so a replaced image lands on both the moment the query is invalidated.
 *
 * **`aria-hidden` in both branches, and the `alt` is empty.** The name is beside
 * the face in text at every call site; announcing it twice is the defect the
 * subscriber pack's account face already avoids. A portrait here is decoration
 * for an identity the document already states.
 *
 * `object-cover` on a `size-full` image inside a fixed box: the crop is centred
 * square at the origin, so the box never letterboxes and the image never sets
 * the box's own size.
 */
export function PersonFace({
  className,
  portraitUrl,
}: {
  className?: string;
  /** The stored `portrait_url`, already resolved against the asset origin. */
  portraitUrl?: string | null;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-grid shrink-0 place-items-center overflow-hidden rounded-full border border-border',
        '[background:linear-gradient(135deg,color-mix(in_srgb,var(--brand-noir)_80%,var(--brand-bone)_20%),color-mix(in_srgb,var(--brand-noir)_92%,var(--brand-bone)_8%))]',
        className,
      )}
    >
      {portraitUrl ? <img src={portraitUrl} alt="" className="size-full object-cover" /> : null}
    </span>
  );
}

/**
 * The distinct roles across a person's credits.
 *
 * **Derived, never authored.** A credit is a relation between a person and a
 * title *with a role*, so the role belongs to the relation — which is why
 * neither the create dialog nor the person's own form carries a role field, and
 * why this set is computed from the credits rather than read off the profile.
 *
 * All `neutral`: a role is not a state and takes no colour of its own.
 */
export function RoleBadges({ roles, empty }: { roles: readonly string[]; empty: string }) {
  if (roles.length === 0) {
    // A cell that says what is absent, rather than an empty cell in a table of
    // populated rows — which reads as missing data instead of as a true zero.
    return <span className="text-xs text-ink-muted">{empty}</span>;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {roles.map((role) => (
        <StateBadge key={role}>{roleLabel(role)}</StateBadge>
      ))}
    </span>
  );
}

/**
 * The distinct role tokens across a credit set, in the order they first appear.
 *
 * First-appearance order rather than alphabetical: the rows arrive newest-first
 * from the service, so the badge nearest the name is the role this person is
 * working in now.
 */
export function distinctRoles(items: readonly CreditedItemRow[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const item of items) {
    if (seen.has(item.role)) continue;
    seen.add(item.role);
    ordered.push(item.role);
  }

  return ordered;
}

/** How many distinct titles a credit set covers — a title with two roles counts once. */
export function distinctTitleCount(items: readonly CreditedItemRow[]): number {
  return new Set(items.map((item) => item.id)).size;
}

/**
 * How many rows the surface is showing.
 *
 * **Not an empty state and not a pager.** No catalog row gives this register a
 * traversal control, so it states its own truncation rather than inventing one —
 * and it states it always, because a line that appears only on truncation
 * teaches an operator to read its absence as completeness.
 *
 * It says how many are **shown** and nothing more. The register's route returns
 * a bare array with no total beside it, so «of how many» is a figure this
 * surface has not read — and printing one it inferred would be inventing the
 * only number on the line that matters.
 */
export function ShownLine({ shown, note, more }: { shown: number; note: string; more?: string }) {
  return (
    <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
      <bdi>{formatNumber(shown)}</bdi> {note}
      {more ? ` ${more}` : null}
    </p>
  );
}

/** A section — its heading row with its one action, then its body. */
export function PeopleSection({
  id,
  heading,
  action,
  context,
  children,
}: {
  id: string;
  heading: string;
  action?: ReactNode;
  /** The one consequence the operator cannot see from the screen. */
  context?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-section-gap first:mt-0">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={id} className="font-display text-h2 font-bold">
          {heading}
        </h2>
        {action ? <span className="ms-auto">{action}</span> : null}
      </div>

      {context ? <p className="mb-4 max-w-measure text-xs text-ink-muted">{context}</p> : null}

      {children}
    </section>
  );
}

/** The secondary verb that sits on a section's heading row. */
export function SectionAction({
  onClick,
  icon,
  children,
}: {
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hit-target flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
    >
      {icon}
      {children}
    </button>
  );
}
